/**
 * Monthly payment calculator runtime.
 *
 * Supports product-backed readonly pricing and standalone editable pricing
 * through the same section-scoped lifecycle owner.
 */

(() => {
  const RUNTIME_KEY = '__MarineMaxMonthlyPaymentCalculator';
  const INSTANCE_KEY = '__monthlyPaymentCalculatorInstance';
  const ROOT_SELECTOR = '[data-monthly-payment-calculator]';
  const CONFIG_SELECTOR = '#mm-pricing-summary-config';
  const OUT_OF_RANGE_CLASS = 'mm-payment-out-of-range';
  const ACTIVE_DOWN_PAYMENT_CLASS = 'down-payment__option--active';
  const HIDDEN_CLASS = '!hidden';
  const DEFAULT_RATE_ENDPOINT =
    'https://integrate.marinemax.com/finance/GetRate';
  const DEFAULT_FALLBACK_APR = 8.5;

  const existingRuntime = window[RUNTIME_KEY];
  if (existingRuntime?.initialize) {
    existingRuntime.initialize(document);
    return;
  }

  function parseInteger(value, fallback = 0) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseFloatValue(value, fallback) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseBoatPriceToCents(value) {
    const digits = String(value ?? '').replace(/[^0-9]/g, '');
    if (!digits) return 0;

    const dollars = Number.parseInt(digits, 10);
    return Number.isFinite(dollars) && dollars > 0 ? dollars * 100 : 0;
  }

  function parseMonthlyPayment(value) {
    if (value == null || value === '') return null;

    const parsed = Number.parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function formatUsdNoCents(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  function estimateMonthlyPayment(
    principalDollars,
    annualRatePercent,
    termMonths,
  ) {
    if (
      !principalDollars ||
      principalDollars <= 0 ||
      !termMonths ||
      termMonths <= 0
    ) {
      return '';
    }

    const monthlyRate = annualRatePercent / 100 / 12;
    if (monthlyRate === 0) {
      return formatUsdNoCents(principalDollars / termMonths);
    }

    const compound = Math.pow(1 + monthlyRate, termMonths);
    const payment =
      (principalDollars * (monthlyRate * compound)) / (compound - 1);
    return formatUsdNoCents(payment);
  }

  function mapFicoToCreditRating(value) {
    const ficoScore = Number.parseInt(value, 10);
    if (!Number.isFinite(ficoScore)) return null;
    if (ficoScore >= 750) return 'excellent';
    if (ficoScore >= 700) return 'good';
    if (ficoScore >= 650) return 'fair';
    return 'poor';
  }

  class MonthlyPaymentCalculator {
    constructor(root) {
      if (!root) return;
      if (root[INSTANCE_KEY]) return root[INSTANCE_KEY];

      this.root = root;
      this.elements = this.collectElements();
      if (!this.hasRequiredElements()) return;

      this.eventAbortController = new AbortController();
      this.requestAbortController = null;
      this.calculationVersion = 0;
      this.isDestroyed = false;
      this.priceCents = parseInteger(
        this.elements.priceSource.getAttribute('data-price'),
        0,
      );
      this.downPaymentDefaultValue =
        this.elements.downPaymentDisplay.getAttribute(
          'data-down-payment-default',
        ) || '20';
      this.loanDurationDefaultValue =
        this.elements.loanDurationSelect.getAttribute(
          'data-loan-duration-default',
        ) || '20';
      this.creditRatingDefaultValue =
        this.elements.creditRatingSelect.getAttribute(
          'data-credit-rating-default',
        ) || '800';
      this.defaultPriceCents = this.getDefaultPriceCents();
      this.downPaymentSelectedValue = this.downPaymentDefaultValue;

      root[INSTANCE_KEY] = this;
      this.initialize();
    }

    collectElements() {
      return {
        boatPriceDisplay: this.root.querySelector('[data-boat-price-display]'),
        boatPriceInput: this.root.querySelector('[data-boat-price-input]'),
        calculateButton: this.root.querySelector('[data-calculate-button]'),
        columns: this.root.querySelector('.monthly-payment-calculator__columns'),
        creditRatingDisplay: this.root.querySelector(
          '[data-credit-rating-display]',
        ),
        creditRatingSelect: this.root.querySelector(
          '[data-credit-rating-select]',
        ),
        downPaymentDisplay: this.root.querySelector(
          '[data-down-payment-amount]',
        ),
        downPaymentOptions: Array.from(
          this.root.querySelectorAll('.down-payment__option'),
        ),
        loanAmountDisplay: this.root.querySelector(
          '[data-loan-amount-display]',
        ),
        loanDurationSelect: this.root.querySelector(
          '[data-loan-duration-select]',
        ),
        monthlyPaymentDisplay: this.root.querySelector(
          '[data-monthly-payment-display]',
        ),
        outOfRangeMessage: this.root.querySelector(
          '[data-mm-calculator-oor]',
        ),
        priceSource: this.root.querySelector('[data-down-payment-selector]'),
        resetButton: this.root.querySelector('[data-reset-button]'),
        summary: this.root.querySelector(
          '.monthly-payment-calculator__summary',
        ),
        summaryError: this.root.querySelector(
          '.monthly-payment-calculator__summary__error',
        ),
      };
    }

    hasRequiredElements() {
      const requiredKeys = [
        'calculateButton',
        'creditRatingDisplay',
        'creditRatingSelect',
        'downPaymentDisplay',
        'loanAmountDisplay',
        'loanDurationSelect',
        'monthlyPaymentDisplay',
        'priceSource',
        'resetButton',
        'summary',
        'summaryError',
      ];
      return requiredKeys.every((key) => Boolean(this.elements[key]));
    }

    getDefaultPriceCents() {
      const inputValue = this.elements.boatPriceInput?.getAttribute(
        'data-default-price-cents',
      );
      const parsed = Number.parseInt(inputValue, 10);
      return Number.isFinite(parsed) ? parsed : this.priceCents;
    }

    initialize() {
      const listenerOptions = { signal: this.eventAbortController.signal };

      this.elements.downPaymentOptions.forEach((option) => {
        this.setDownPaymentOptionState(option);
        option.addEventListener(
          'click',
          () => this.selectDownPayment(option),
          listenerOptions,
        );
      });

      this.elements.resetButton.addEventListener(
        'click',
        () => this.reset(),
        listenerOptions,
      );
      this.elements.calculateButton.addEventListener(
        'click',
        () => this.calculateAndTrack(),
        listenerOptions,
      );

      if (this.elements.boatPriceInput) {
        this.elements.boatPriceInput.addEventListener(
          'input',
          () => this.syncBoatPriceDisplays(),
          listenerOptions,
        );
        this.elements.boatPriceInput.addEventListener(
          'blur',
          () => this.commitBoatPriceInput(),
          listenerOptions,
        );
        this.elements.boatPriceInput.addEventListener(
          'change',
          () => this.commitBoatPriceInput(),
          listenerOptions,
        );
        this.elements.boatPriceInput.value = formatUsdNoCents(
          this.getCurrentPriceCents() / 100,
        );
      }

      this.syncBoatPriceDisplays();
      this.displayMonthlyPayment();
    }

    readSharedConfig() {
      const section = this.root.closest?.('[id^="shopify-section"]');
      const node =
        section?.querySelector(CONFIG_SELECTOR) ||
        document.querySelector(CONFIG_SELECTOR);
      if (!node) return {};

      try {
        return JSON.parse(node.textContent || '{}');
      } catch (_) {
        return {};
      }
    }

    setDownPaymentOptionState(option) {
      option.classList.toggle(
        ACTIVE_DOWN_PAYMENT_CLASS,
        option.getAttribute('data-value') === this.downPaymentSelectedValue,
      );
    }

    selectDownPayment(option) {
      this.downPaymentSelectedValue =
        option.getAttribute('data-value') || this.downPaymentDefaultValue;
      this.elements.downPaymentOptions.forEach((candidate) =>
        this.setDownPaymentOptionState(candidate),
      );
      this.elements.downPaymentDisplay.textContent = formatUsdNoCents(
        this.computeDownPaymentDollars(),
      );
    }

    computeDownPaymentDollars() {
      const percentage = parseInteger(
        this.downPaymentSelectedValue,
        parseInteger(this.downPaymentDefaultValue, 20),
      );
      return Math.round(
        (this.getCurrentPriceCents() / 100) * (percentage / 100),
      );
    }

    getCurrentPriceCents() {
      if (!this.elements.boatPriceInput) return this.priceCents;

      const parsed = parseBoatPriceToCents(this.elements.boatPriceInput.value);
      return parsed > 0 ? parsed : this.defaultPriceCents;
    }

    syncBoatPriceDisplays() {
      const currentPriceCents = this.getCurrentPriceCents();
      if (this.elements.boatPriceDisplay) {
        this.elements.boatPriceDisplay.textContent = formatUsdNoCents(
          currentPriceCents / 100,
        );
      }
      this.elements.downPaymentDisplay.textContent = formatUsdNoCents(
        this.computeDownPaymentDollars(),
      );
    }

    commitBoatPriceInput() {
      this.priceCents = this.getCurrentPriceCents();
      this.elements.boatPriceInput.value = formatUsdNoCents(
        this.priceCents / 100,
      );
      this.syncBoatPriceDisplays();
    }

    reset() {
      this.resetBoatPrice();
      this.resetDownPayment();
      this.resetSelect(
        this.elements.loanDurationSelect,
        this.loanDurationDefaultValue,
      );
      this.resetSelect(
        this.elements.creditRatingSelect,
        this.creditRatingDefaultValue,
      );
      this.displayMonthlyPayment();
    }

    resetBoatPrice() {
      if (!this.elements.boatPriceInput) return;
      this.elements.boatPriceInput.value = formatUsdNoCents(
        this.defaultPriceCents / 100,
      );
      this.syncBoatPriceDisplays();
    }

    resetDownPayment() {
      this.downPaymentSelectedValue = this.downPaymentDefaultValue;
      this.elements.downPaymentOptions.forEach((option) =>
        this.setDownPaymentOptionState(option),
      );
      this.elements.downPaymentDisplay.textContent = formatUsdNoCents(
        this.computeDownPaymentDollars(),
      );
    }

    resetSelect(select, value) {
      select.value = value;
      select.dispatchEvent(new Event('change'));
    }

    getSelectedCreditRatingLabel() {
      const { creditRatingSelect } = this.elements;
      const selectedOption =
        creditRatingSelect.options[creditRatingSelect.selectedIndex];
      return selectedOption ? selectedOption.text : '';
    }

    buildRateRequest(loanAmountDollars) {
      const loanDurationYears =
        this.elements.loanDurationSelect.value || '20';
      const creditRating = this.elements.creditRatingSelect.value || '800';
      const percentDown = parseInteger(this.downPaymentSelectedValue, 20);

      return {
        ficoScore: creditRating,
        percentDown,
        term: parseInteger(loanDurationYears, 20) * 12,
        loanAmount: parseInteger(loanAmountDollars, 0),
      };
    }

    async fetchRate(loanAmountDollars, signal) {
      const config = this.readSharedConfig();
      const endpoint =
        typeof config.rateEndpoint === 'string' && config.rateEndpoint.trim()
          ? config.rateEndpoint
          : DEFAULT_RATE_ENDPOINT;

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.buildRateRequest(loanAmountDollars)),
          signal,
        });

        if (!response.ok) {
          return { Rate: '', MonthlyPayment: '', ResultCode: 'NF' };
        }
        return await response.json();
      } catch (_) {
        if (signal.aborted) return null;
        return { Rate: '', MonthlyPayment: '', ResultCode: 'NF' };
      }
    }

    buildMonthlyPaymentText(data, loanAmountDollars) {
      const apiSuccess =
        data && (data.ResultCode === 'F' || data.ResultCode === 'f');
      if (apiSuccess) {
        const apiMonthlyPayment =
          data.MonthlyPayment ||
          data.monthlyPayment ||
          data.monthly_payment ||
          '';
        const parsedPayment = parseMonthlyPayment(apiMonthlyPayment);
        if (parsedPayment != null) return formatUsdNoCents(parsedPayment);
        if (apiMonthlyPayment) return apiMonthlyPayment;
      }

      const rate =
        data && (data.Rate || data.rate)
          ? Number.parseFloat(data.Rate || data.rate)
          : DEFAULT_FALLBACK_APR;
      const loanDurationMonths =
        parseInteger(this.elements.loanDurationSelect.value, 20) * 12;
      return (
        estimateMonthlyPayment(loanAmountDollars, rate, loanDurationMonths) ||
        '—'
      );
    }

    setOutOfRangeState(isOutOfRange) {
      this.elements.columns?.toggleAttribute('hidden', isOutOfRange);
      this.elements.outOfRangeMessage?.toggleAttribute(
        'hidden',
        !isOutOfRange,
      );

      const pricingSummaryRuntime =
        window.__MarineMaxProductPricingSummary;
      if (pricingSummaryRuntime?.setCalculatorOutOfRangeState) {
        pricingSummaryRuntime.setCalculatorOutOfRangeState(
          this,
          isOutOfRange,
        );
      } else {
        document.body.classList.toggle(OUT_OF_RANGE_CLASS, isOutOfRange);
      }
    }

    async displayMonthlyPayment() {
      const calculationVersion = ++this.calculationVersion;
      this.requestAbortController?.abort();
      const requestAbortController = new AbortController();
      this.requestAbortController = requestAbortController;

      this.priceCents = this.getCurrentPriceCents();
      const percentDown = parseInteger(this.downPaymentSelectedValue, 20);
      const loanAmountDollars = Math.round(
        (this.priceCents / 100) * (1 - percentDown / 100),
      );
      const data = await this.fetchRate(
        String(loanAmountDollars),
        requestAbortController.signal,
      );

      if (
        this.isDestroyed ||
        requestAbortController.signal.aborted ||
        calculationVersion !== this.calculationVersion ||
        !data
      ) {
        return { completed: false, outOfRange: false };
      }
      if (isSupersededRoot(this.root)) {
        this.destroy();
        return { completed: false, outOfRange: false };
      }
      if (this.requestAbortController === requestAbortController) {
        this.requestAbortController = null;
      }

      const monthlyPaymentText = this.buildMonthlyPaymentText(
        data,
        loanAmountDollars,
      );
      const config = this.readSharedConfig();
      const floor = parseFloatValue(config.floor, 200);
      const ceiling = parseFloatValue(config.ceiling, 5000);
      const monthlyPayment = parseMonthlyPayment(monthlyPaymentText);
      const outOfRange =
        monthlyPayment != null &&
        (monthlyPayment < floor || monthlyPayment > ceiling);

      if (outOfRange) {
        this.setOutOfRangeState(true);
        this.elements.summaryError.classList.add(HIDDEN_CLASS);
        this.elements.monthlyPaymentDisplay.textContent = '';
        return { completed: true, outOfRange: true };
      }

      this.setOutOfRangeState(false);
      this.elements.summaryError.classList.add(HIDDEN_CLASS);
      this.elements.summary.classList.remove(HIDDEN_CLASS);
      this.elements.monthlyPaymentDisplay.textContent = monthlyPaymentText;
      if (this.elements.boatPriceDisplay) {
        this.elements.boatPriceDisplay.textContent = formatUsdNoCents(
          this.priceCents / 100,
        );
      }
      this.elements.loanAmountDisplay.textContent = formatUsdNoCents(
        loanAmountDollars,
      );
      this.elements.creditRatingDisplay.textContent =
        this.getSelectedCreditRatingLabel();

      return { completed: true, outOfRange: false };
    }

    readAnalyticsPayload() {
      this.priceCents = this.getCurrentPriceCents();
      const percentDown = parseInteger(this.downPaymentSelectedValue, 20);
      const loanAmountDollars = Math.round(
        (this.priceCents / 100) * (1 - percentDown / 100),
      );
      const termMonths =
        parseInteger(this.elements.loanDurationSelect.value, 20) * 12;

      return {
        down_payment: Math.round(
          (this.priceCents / 100) * (percentDown / 100),
        ),
        loan_duration: `${termMonths}_months`,
        credit_rating: mapFicoToCreditRating(
          this.elements.creditRatingSelect.value,
        ),
        loan_amount: loanAmountDollars,
        monthly_payment: parseMonthlyPayment(
          this.elements.monthlyPaymentDisplay.textContent || '',
        ),
      };
    }

    pushSubmitAnalytics(payload) {
      window.dataLayer = window.dataLayer || [];
      const itemName = window.MarineMaxProduct?.item_name || null;
      window.dataLayer.push({
        event: 'calculator_submit',
        calculator_type: 'payment',
        down_payment:
          payload.down_payment != null ? payload.down_payment : null,
        loan_duration: payload.loan_duration || null,
        credit_rating: payload.credit_rating || null,
        loan_amount: payload.loan_amount != null ? payload.loan_amount : null,
        monthly_payment:
          payload.monthly_payment != null ? payload.monthly_payment : null,
        item_name: itemName,
      });
    }

    async calculateAndTrack() {
      const result = await this.displayMonthlyPayment();
      if (
        !result.completed ||
        result.outOfRange ||
        document.body.classList.contains(OUT_OF_RANGE_CLASS)
      ) {
        return;
      }

      try {
        this.pushSubmitAnalytics(this.readAnalyticsPayload());
      } catch (_) {
        // Analytics must never block the calculator.
      }
    }

    destroy() {
      if (this.isDestroyed) return;
      this.setOutOfRangeState(false);
      this.isDestroyed = true;
      this.calculationVersion += 1;
      this.requestAbortController?.abort();
      this.requestAbortController = null;
      this.eventAbortController.abort();

      if (this.root?.[INSTANCE_KEY] === this) {
        delete this.root[INSTANCE_KEY];
      }
    }
  }

  function collectRoots(root = document) {
    if (!root) return [];

    const roots = [];
    if (typeof root.matches === 'function' && root.matches(ROOT_SELECTOR)) {
      roots.push(root);
    }
    if (typeof root.querySelectorAll === 'function') {
      roots.push(...root.querySelectorAll(ROOT_SELECTOR));
    }
    return [...new Set(roots)];
  }

  function isSupersededRoot(root) {
    if (root.isConnected === false) return true;
    return Boolean(
      root.closest?.(
        'main[style*="display: none"], product-info[style*="display: none"]',
      ),
    );
  }

  function initialize(root = document) {
    return collectRoots(root)
      .filter((calculatorRoot) => {
        if (!isSupersededRoot(calculatorRoot)) return true;
        calculatorRoot[INSTANCE_KEY]?.destroy?.();
        return false;
      })
      .map(
        (calculatorRoot) =>
          calculatorRoot[INSTANCE_KEY] ||
          new MonthlyPaymentCalculator(calculatorRoot),
      )
      .filter((instance) => instance?.root?.[INSTANCE_KEY] === instance);
  }

  function destroy(root) {
    collectRoots(root).forEach((calculatorRoot) => {
      calculatorRoot[INSTANCE_KEY]?.destroy?.();
    });
  }

  const runtime = Object.freeze({
    MonthlyPaymentCalculator,
    destroy,
    initialize,
    utilities: Object.freeze({
      estimateMonthlyPayment,
      formatUsdNoCents,
      mapFicoToCreditRating,
      parseBoatPriceToCents,
      parseMonthlyPayment,
    }),
  });
  Object.defineProperty(window, RUNTIME_KEY, {
    configurable: true,
    enumerable: false,
    value: runtime,
    writable: false,
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(document),
      { once: true },
    );
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) =>
    initialize(event.target),
  );
  document.addEventListener('shopify:section:unload', (event) =>
    destroy(event.target),
  );
})();
